import { ReactNode, useRef, useState, useMemo, useEffect } from 'react';

import { Input } from 'antd';
import {
  Button,
  ButtonType,
  CaretDownOutlined,
  Checkbox,
  CheckboxGroup,
  Column,
  Icon,
  Notification,
  Popover,
  PopoverPlacement,
  Row,
  SearchOutlined,
  Space,
  Text,
  Tooltip,
} from 'farmhub-ui-core';

import { useAppSelector } from 'app';
import { selectAppDevice } from 'slice';
import { removeVietnameseTones } from 'utils';

import {
  CheckboxWrapper,
  ProvinceWrapper,
  Wrapper,
  WrapperCheckBox,
  WrapperChildCheckBox,
} from './styles';

type ColumnOptionType = {
  value: any;
  title: string;
  checked?: boolean;
  parent?: {
    value: any;
    title: string;
    checked?: boolean;
  };
};

type MultiSelectorProps = {
  name: string;
  tooltip?: string;
  childName?: string;
  containerID?: string;
  optionSelected?: string[];
  children?: ReactNode;
  options: ColumnOptionType[];
  maximumSelectedNumber?: number;
  type?: 'GROUP' | 'LIST';
  onSelected?: (values: string[]) => any;
};

const MultiSelector = ({
  name,
  options,
  children,
  tooltip,
  type = 'LIST',
  containerID = '',
  childName = 'mục con',
  optionSelected = ['-1'],
  maximumSelectedNumber,
  onSelected,
}: MultiSelectorProps): JSX.Element => {
  const spacePressedRef = useRef<any>(null);
  const [visible, setVisible] = useState(false);
  const [searchName, setSearchName] = useState<string>('');
  const [selectedOption, setSelectedOption] =
    useState<string[]>(optionSelected);
  const [selectedParent, setSelectedParent] = useState<any[]>([]);
  const [selectedChild, setSelectChild] = useState<any[]>([]);

  const appDevice = useAppSelector(selectAppDevice);

  const optionSelectText = useMemo(() => {
    if (selectedOption.includes('-1')) {
      return `Tất cả ${name}`;
    }

    if (type == 'GROUP') {
      return `${selectedParent.length} ${name}, ${selectedChild.length} ${childName}`;
    }

    return `${selectedOption.length} ${name}`;
  }, [selectedOption, selectedChild, selectedParent, type]);

  const handleSearch = (event: any) => {
    const code = event.keyCode ? event.keyCode : event.which;
    if (code != 13) {
      setSearchName(event.target.value);
    }
  };

  const handleVisibleChange = (isVisible: boolean) => {
    if (spacePressedRef.current) {
      spacePressedRef.current = false;
      setVisible(true);
    } else {
      setVisible(isVisible);
    }
  };

  const handleKeyUp = (event: any) => {
    if (event.which === 32) {
      spacePressedRef.current = true;
    }
  };

  const onClickAccept = () => {
    if (selectedOption.length !== 0) {
      onSelected && onSelected(selectedOption);

      setVisible(false);
    } else {
      Notification(
        'info',
        `Vui lòng chọn ít nhất 1 ${name}!`,
        '',
        appDevice.device
      );
    }
  };

  const onClickCancel = () => {
    if (optionSelected?.length) {
      setSelectedOption(optionSelected);
    } else {
      setSelectedOption(['-1']);
    }

    setVisible(false);
  };

  const dataOptions: any[] = useMemo(() => {
    if (type === 'LIST') {
      if (searchName) {
        return options.map(item => {
          const isShow = removeVietnameseTones(item.title)
            .toLowerCase()
            .includes(removeVietnameseTones(searchName).toLowerCase());
          return { ...item, show: isShow };
        });
      }
      return options.map(item => {
        return { ...item, show: true };
      });
    }
    if (type === 'GROUP') {
      let formatOptions: any[] = [];
      const groupedData = {};

      if (searchName) {
        formatOptions = options.map(item => {
          const isShow =
            removeVietnameseTones(item.title)
              .toLowerCase()
              .includes(removeVietnameseTones(searchName).toLowerCase()) ||
            (item?.parent?.title &&
              removeVietnameseTones(item?.parent?.title || '')
                .toLowerCase()
                .includes(removeVietnameseTones(searchName).toLowerCase()));

          return { ...item, show: isShow };
        });
      } else {
        formatOptions = options.map(item => {
          return { ...item, show: true };
        });
      }

      formatOptions.forEach(item => {
        if (item.parent && item.parent.value) {
          const parentValue = item.parent.value;
          if (!groupedData[parentValue]) {
            groupedData[parentValue] = {
              value: parentValue,
              title: item.parent.title,
              show: true,
              indeterminate: false,
              children: [],
            };
          }
          groupedData[parentValue].children.push(item);
        }
      });

      if (searchName) {
        formatOptions.forEach(item => {
          const parentValue = item.parent?.value;
          if (parentValue) {
            const isShowChildList = groupedData[parentValue].children
              .map(childItem => childItem.show)
              .some(childItem => childItem == true);
            if (!isShowChildList) {
              groupedData[parentValue].show = false;
            }
          }
        });
      }

      formatOptions.forEach(item => {
        const parentValue = item.parent?.value;
        if (parentValue) {
          const childrenIDs = groupedData[parentValue].children.map(
            childItem => childItem.value
          );

          const foundExistedIDs: string[] = childrenIDs.filter(childrenID =>
            selectedChild.includes(childrenID)
          );

          const isIndeterminate =
            foundExistedIDs.length < childrenIDs.length &&
            foundExistedIDs.length != 0;

          if (isIndeterminate) {
            groupedData[parentValue].indeterminate = true;
          } else {
            groupedData[parentValue].indeterminate = false;
          }
        }
      });

      // Convert groupedData object to an array of parent objects
      const result = Object.values(groupedData);

      return result;
    }

    return [];
  }, [options, searchName, type, selectedChild]);

  const parentOptionObject = useMemo(() => {
    if (type == 'GROUP') {
      const parentObjectArray = dataOptions.reduce((acc, parent) => {
        acc[parent.value] = parent;
        return acc;
      }, {});
      return parentObjectArray;
    }
    return {};
  }, [dataOptions, type]);

  const childOptionObject: Record<string, ColumnOptionType> = useMemo(() => {
    if (type == 'GROUP') {
      const childObjectArray = options.reduce((acc, child) => {
        acc[child.value] = child;
        return acc;
      }, {});
      return childObjectArray;
    }
    return {};
  }, [options, type]);

  useEffect(() => {
    setSelectedOption(optionSelected);
  }, [optionSelected]);

  useEffect(() => {
    if (type == 'GROUP') {
      const parentIDs: string[] = [];
      const childIDs: string[] = [];
      optionSelected.forEach(item => {
        const parentOptionData = parentOptionObject[item];
        const isSelectedParent = !!parentOptionData;

        if (isSelectedParent) {
          parentIDs.push(item);
        } else {
          childIDs.push(item);
        }
      });
      setSelectChild(childIDs);
      setSelectedParent(parentIDs);
    }
  }, [optionSelected, type, childOptionObject]);

  const handleChangeWithGroup = (values: string[]) => {
    let selectedIDs: string[] = [];
    let selectedParentIDs: string[] = [];
    let selectedChildIDs: string[] = [];

    // Check values selected
    values.forEach(item => {
      const isNewSelected = !selectedOption?.includes(item);

      const parentOptionData = parentOptionObject[item];
      const isSelectedParent = !!parentOptionData;

      if (isSelectedParent) {
        selectedIDs.push(item);
        selectedParentIDs.push(item);
        const childrenIDs: string[] = parentOptionData.children.map(
          childItem => childItem.value
        );

        const foundExistedIDs: string[] = childrenIDs.filter(childrenID =>
          values.includes(childrenID)
        );

        // Selected all entity sub category
        if (foundExistedIDs.length == 0 || isNewSelected) {
          selectedIDs.push(...childrenIDs);
          selectedChildIDs.push(...childrenIDs);
        } else {
          selectedIDs.push(...foundExistedIDs);
          selectedChildIDs.push(...foundExistedIDs);
        }
      } else {
        if (!selectedIDs.includes(item) && item != '-1') {
          const foundChild = childOptionObject[item];

          const parentOptionDataForSelectedChildCheckBox =
            parentOptionObject[foundChild?.parent?.value];

          const unCheckChildrenIDs: string[] =
            parentOptionDataForSelectedChildCheckBox.children.map(
              childItem => childItem.value
            );

          const foundAppearIDs: string[] = values.filter(childrenID =>
            unCheckChildrenIDs.includes(childrenID)
          );

          if (foundAppearIDs.length === unCheckChildrenIDs.length) {
            selectedIDs.push(foundChild?.parent?.value);
            selectedParentIDs.push(foundChild?.parent?.value);
          }

          selectedIDs.push(item);
          selectedChildIDs.push(item);
        }
      }
    });

    // Check old selected
    const filterSelectedNotHasAll = selectedOption.filter(item => item != '-1');
    if (filterSelectedNotHasAll.length != 0) {
      const unCheckedSelectedIDs: string[] = [
        ...filterSelectedNotHasAll,
      ].filter(item => !values.includes(item));

      unCheckedSelectedIDs.forEach(unCheckedSelectedID => {
        const parentOptionData = parentOptionObject[unCheckedSelectedID];
        const isSelectedParent = !!parentOptionData;

        // Check selected entity category
        if (isSelectedParent) {
          const childrenIDs: string[] = parentOptionData.children.map(
            item => item.value
          );
          selectedIDs = selectedIDs.filter(
            item => ![...childrenIDs, unCheckedSelectedID].includes(item)
          );
          selectedChildIDs = selectedChildIDs.filter(
            item => !childrenIDs.includes(item)
          );
          selectedParentIDs = selectedParentIDs.filter(
            item => unCheckedSelectedID != item
          );
        } else {
          const foundChild = childOptionObject[unCheckedSelectedID];

          const parentOptionDataForSelectedChildCheckBox =
            parentOptionObject[foundChild?.parent?.value];
          const unCheckChildrenIDs: string[] =
            parentOptionDataForSelectedChildCheckBox.children.map(
              item => item.value
            );

          const foundAppearIDs: string[] = values.filter(childrenID =>
            unCheckChildrenIDs.includes(childrenID)
          );

          if (foundAppearIDs.length === 0) {
            selectedIDs = selectedIDs.filter(
              item =>
                ![foundChild?.parent?.value, ...unCheckChildrenIDs].includes(
                  item
                )
            );
            selectedChildIDs = selectedChildIDs.filter(
              item => ![...unCheckChildrenIDs].includes(item)
            );
            selectedParentIDs = selectedParentIDs.filter(
              item => foundChild?.parent?.value != item
            );
          }
        }
      });
    }

    if (values.length == 0 || selectedIDs.length == 0) {
      setSelectedOption(['-1']);
      setSelectChild([]);
      setSelectedParent([]);
    } else {
      if (values.slice(-1)[0] === '-1') {
        setSelectedOption(['-1']);
        setSelectChild([]);
        setSelectedParent([]);
      } else {
        const uniqueSelectOptions = selectedIDs.filter(
          (item, pos) => selectedIDs.indexOf(item) == pos
        );
        const uniqueSelectParentOptions = selectedParentIDs.filter(
          (item, pos) => selectedParentIDs.indexOf(item) == pos
        );
        const uniqueSelectChildOptions = selectedChildIDs.filter(
          (item, pos) => selectedChildIDs.indexOf(item) == pos
        );
        setSelectedOption(uniqueSelectOptions.filter(item => item != '-1'));
        setSelectedParent(
          uniqueSelectParentOptions.filter(item => item != '-1')
        );
        setSelectChild(uniqueSelectChildOptions.filter(item => item != '-1'));
      }
    }
  };

  const handleChange = (values: any) => {
    if (type == 'GROUP') {
      handleChangeWithGroup(values);
    }

    if (type == 'LIST') {
      if (values.length == 0) {
        setSelectedOption(['-1']);
      } else {
        if (values.slice(-1)[0] === '-1') {
          setSelectedOption(['-1']);
        } else {
          if (values[0] === '-1') {
            setSelectedOption(values.filter((item: string) => item !== '-1'));
          } else {
            setSelectedOption(values);
          }
        }
      }
    }
  };

  const renderMenu = useMemo(
    () => (
      <Wrapper>
        <Text color="primary" weight="bold">
          Chọn {name}
        </Text>
        <Space height={12} />
        <Input
          height="42px"
          placeholder={`Tên ${name}`}
          prefix={
            <Icon>
              <SearchOutlined style={{ fontSize: '18px' }} />
            </Icon>
          }
          value={searchName}
          onChange={handleSearch}
          onKeyUp={handleKeyUp}
        />
        <Space height={12} />
        <Row justifyContent="space-between">
          <Row width="auto">
            <Text color="secondary">Đã chọn:</Text>
            <Space width={4} />
            <Text color="primary">{optionSelectText}</Text>
          </Row>
        </Row>
        <Space height={12} />

        <ProvinceWrapper>
          <CheckboxWrapper>
            <CheckboxGroup value={selectedOption} onChange={handleChange}>
              <Checkbox key="multiCheckBoxAll" value="-1">
                <Text>Tất cả {name}</Text>
              </Checkbox>

              {type == 'GROUP' ? (
                <>
                  {dataOptions.map((item: any) => (
                    <>
                      <WrapperCheckBox
                        key={`multiCheckBox${item.value}`}
                        indeterminate={item.indeterminate}
                        show={item.show}
                        value={item.value}
                      >
                        <Text>{item.title}</Text>
                      </WrapperCheckBox>
                      <>
                        {item.children.map((childItem: any) => (
                          <>
                            <WrapperChildCheckBox
                              key={`childMultiCheckBox${childItem.value}`}
                              show={childItem.show}
                              value={childItem.value}
                            >
                              <Text>{childItem.title}</Text>
                            </WrapperChildCheckBox>
                          </>
                        ))}
                      </>
                    </>
                  ))}
                </>
              ) : null}

              {type == 'LIST' ? (
                <>
                  {dataOptions.map((item: any) => (
                    <WrapperCheckBox
                      key={`multiCheckBox${item.value}`}
                      show={item.show}
                      value={item.value}
                    >
                      {item.title}
                    </WrapperCheckBox>
                  ))}
                </>
              ) : null}
            </CheckboxGroup>
          </CheckboxWrapper>
        </ProvinceWrapper>

        <Space height={5} />
        <Row justifyContent="space-between">
          <Column width="48%">
            <Button
              type={ButtonType.Default}
              width="100%"
              onClick={onClickCancel}
            >
              Hủy bỏ
            </Button>
          </Column>
          <Column width="48%">
            <Button
              disabled={
                !!(
                  maximumSelectedNumber != undefined &&
                  maximumSelectedNumber < selectedOption.length
                )
              }
              type={ButtonType.Primary}
              width="100%"
              onClick={onClickAccept}
            >
              Áp dụng
            </Button>
          </Column>
        </Row>
      </Wrapper>
    ),
    [selectedOption, searchName, dataOptions]
  );

  return (
    <Tooltip title={tooltip}>
      <Popover
        containerId={containerID}
        content={renderMenu}
        placement={PopoverPlacement.BottomLeft}
        trigger="click"
        visible={visible}
        onVisibleChange={handleVisibleChange}
      >
        {children || (
          <Button
            height="36px"
            styles={{ 'justify-content': 'space-between', 'flex-shrink': 0 }}
            type={ButtonType.Default}
            width="200px"
          >
            <span>{optionSelectText}</span>
            <CaretDownOutlined color="#BFBFBF" />
          </Button>
        )}
      </Popover>
    </Tooltip>
  );
};

export default MultiSelector;
